import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Database } from '@/../database.types'
import { Label } from '@/components/ui/label'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { CalendarIcon, Plus } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

type TaskAssignmentBlock = Database['public']['Tables']['task_assignment_blocks']['Row'] & {
  users?: Database['public']['Tables']['users']['Row'] | null
}

type ProjectMember = Database['public']['Tables']['project_members']['Row'] & {
  users?: Database['public']['Tables']['users']['Row'] | null
}

interface TaskAssignmentBlocksProps {
  taskId: string
  projectId: string
}

export default function TaskAssignmentBlocks({ taskId, projectId }: TaskAssignmentBlocksProps) {
  const [showNewAssignment, setShowNewAssignment] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [blockStartDate, setBlockStartDate] = useState<Date | undefined>(new Date())
  const [blockEndDate, setBlockEndDate] = useState<Date | undefined>()
  const queryClient = useQueryClient()

  // Fetch all assignment blocks for this task
  const { data: assignmentBlocks = [], isLoading } = useQuery({
    queryKey: ['task-assignment-blocks', taskId],
    queryFn: async (): Promise<TaskAssignmentBlock[]> => {
      const { data, error } = await supabase
        .from('task_assignment_blocks')
        .select(`
          *,
          users!task_assignment_blocks_user_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('task_id', taskId)
        .order('block_start_date', { ascending: false })

      if (error) throw error
      return (data || []) as unknown as TaskAssignmentBlock[]
    }
  })

  // Fetch project members
  const { data: projectMembers = [] } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async (): Promise<ProjectMember[]> => {
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          *,
          users!project_members_user_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('project_id', projectId)

      if (error) throw error
      return (data || []) as unknown as ProjectMember[]
    }
  })

  // Create new assignment block
  const createBlockMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId || !blockStartDate) return

      const { data: { user } } = await supabase.auth.getUser()
      
      const { error } = await supabase
        .from('task_assignment_blocks')
        .insert({
          task_id: taskId,
          user_id: selectedUserId,
          project_id: projectId,
          assigned_by: user?.id,
          block_start_date: blockStartDate.toISOString(),
          block_end_date: blockEndDate?.toISOString() || null,
          assignment_weight: 1.0,
          time_commitment_percentage: 100,
          focus_level: 'normal',
          role_in_task: 'contributor',
          is_active: true
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
      setShowNewAssignment(false)
      setSelectedUserId('')
      setBlockStartDate(new Date())
      setBlockEndDate(undefined)
    }
  })

  // Update assignment block
  const updateBlockMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<TaskAssignmentBlock> }) => {
      const { error } = await supabase
        .from('task_assignment_blocks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
    }
  })

  // End assignment block
  const endBlockMutation = useMutation({
    mutationFn: async (blockId: string) => {
      const { error } = await supabase
        .from('task_assignment_blocks')
        .update({
          block_end_date: new Date().toISOString(),
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', blockId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
    }
  })

  // Delete assignment block
  const deleteBlockMutation = useMutation({
    mutationFn: async (blockId: string) => {
      const { error } = await supabase
        .from('task_assignment_blocks')
        .delete()
        .eq('id', blockId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
    }
  })

  const getInitials = (firstName: string | null, lastName: string | null, email: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    }
    return email.substring(0, 2).toUpperCase()
  }

  // Group blocks by user
  const blocksByUser = assignmentBlocks.reduce((acc, block) => {
    const userId = block.user_id || 'unknown'
    if (!acc[userId]) {
      acc[userId] = []
    }
    acc[userId].push(block)
    return acc
  }, {} as Record<string, TaskAssignmentBlock[]>)

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-base">Assignment Timeline</Label>
        <Button
          size="sm"
          onClick={() => setShowNewAssignment(!showNewAssignment)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Assignment Block
        </Button>
      </div>

      {showNewAssignment && (
        <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Team Member</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent>
                  {projectMembers.map((member) => (
                    <SelectItem key={member.user_id} value={member.user_id!}>
                      {member.users?.first_name && member.users?.last_name
                        ? `${member.users.first_name} ${member.users.last_name}`
                        : member.users?.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !blockStartDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {blockStartDate ? format(blockStartDate, "PP") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={blockStartDate}
                      onSelect={setBlockStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>End Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !blockEndDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {blockEndDate ? format(blockEndDate, "PP") : "Ongoing"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={blockEndDate}
                      onSelect={setBlockEndDate}
                      disabled={(date) => blockStartDate ? date < blockStartDate : false}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowNewAssignment(false)
                setSelectedUserId('')
                setBlockStartDate(new Date())
                setBlockEndDate(undefined)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createBlockMutation.mutate()}
              disabled={!selectedUserId || !blockStartDate || createBlockMutation.isPending}
            >
              Create Assignment Block
            </Button>
          </div>
        </div>
      )}

      <ScrollArea className="h-96">
        <div className="space-y-6">
          {Object.entries(blocksByUser).map(([userId, blocks]) => {
            const user = blocks[0]?.users
            if (!user) return null

            return (
              <div key={userId} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {getInitials(user.first_name, user.last_name, user.email)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium">
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({blocks.length} block{blocks.length !== 1 ? 's' : ''})
                  </span>
                </div>

                <div className="ml-10 space-y-2">
                  {blocks.map((block) => (
                    <div key={block.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              {format(new Date(block.block_start_date), "PP")} - 
                              {block.block_end_date 
                                ? format(new Date(block.block_end_date), "PP")
                                : "Ongoing"}
                            </span>
                            {block.is_active && (
                              <span className="text-green-600 text-xs font-medium">ACTIVE</span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>Weight: {((block.assignment_weight || 1) * 100).toFixed(0)}%</span>
                            <span>Time: {block.time_commitment_percentage || 100}%</span>
                            <span>{block.focus_level || 'normal'} focus</span>
                            <span>{block.role_in_task || 'contributor'}</span>
                          </div>

                          {block.actual_effort_hours && block.actual_effort_hours > 0 && (
                            <div className="text-sm text-gray-600">
                              {block.actual_effort_hours}h logged
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {block.is_active && !block.block_end_date && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => endBlockMutation.mutate(block.id)}
                            >
                              End Block
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteBlockMutation.mutate(block.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {assignmentBlocks.length === 0 && !isLoading && (
            <div className="text-center py-8 text-gray-500">
              No assignment blocks yet. Add one to start tracking contributions.
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}